import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  MenuItem,
  Stack,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import type { RedemptionRecord, SubscriptionRecord } from 'src/pages/admin/services/vaultService';
import ErrorPage403 from 'src/pages/error/403';
import { paths } from 'src/routes/paths';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { formatCount } from '../vaults/helpers';
import { useVaults } from '../vaults/useVaults';
import { VaultSwitcher } from '../vaults/VaultSwitcher';
import {
  ALL_VAULTS,
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_TAB,
  RECORD_TABS,
  RECORDS_NS as NS,
  RECORDS_PERMISSION_RESOURCE as RESOURCE,
  ROWS_PER_PAGE_OPTIONS,
  RecordTab,
} from './constants';
import {
  EMPTY_FILTER,
  RecordsFilter,
  filterKey,
  forTab,
  isRecordTab,
  parseRecordsSearch,
  recordsPath,
  recordsQuery,
  resolveRecordsVault,
  statusesOf,
  toIsoRange,
  toLocalInput,
} from './helpers';
import { RedemptionsTable } from './RedemptionsTable';
import { SubscriptionsTable } from './SubscriptionsTable';
import { useRecords } from './useRecords';

/** What the filter form holds while it is being edited: the URL's values, the range in the browser's zone. */
interface Draft {
  holder: string;
  status: string;
  from: string;
  to: string;
}
const draftOf = (filter: RecordsFilter): Draft => ({
  holder: filter.holderId,
  status: filter.status,
  from: toLocalInput(filter.from),
  to: toLocalInput(filter.to),
});

/**
 * §14.6 Subscriptions & redemptions (XBIT-4245): Finance's two tabs at `/vault/records/:tab`, the filters in the
 * query string (§14.1: state lives in the URL). The vault is pre-selected — the first one, written into the URL — and
 * "all vaults" is an explicit choice. The BFF serves one page at a time, newest first.
 */
const RecordsPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { search } = useLocation();
  const root = paths.dashboard.vaultRecords.root;

  const tabValid = isRecordTab(id);
  const tab: RecordTab = tabValid ? (id as RecordTab) : DEFAULT_TAB;
  const filter = forTab(parseRecordsSearch(search), tab);

  const {
    vaults,
    loading: vaultsLoading,
    loadError: vaultsError,
    refetch: refetchVaults,
  } = useVaults();
  const target = resolveRecordsVault(vaults, filter.vault);
  const settled = !vaultsLoading && !vaultsError;
  const ready = settled && tabValid && !target.redirect && (target.all || !!target.vaultId);

  useEffect(() => {
    if (!settled) return;
    if (tabValid && !target.redirect) return;
    if (target.unknown) notify(translate(`${NS}.unknownVault`, { id: filter.vault }), { type: 'warning' });
    navigate(recordsPath(root, tab, { ...filter, vault: target.redirect ?? filter.vault }), { replace: true });
    // translate / notify are not identity-stable; the redirect depends on the URL and the list only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, tabValid, target.redirect, target.unknown, search, id, root]);

  // The page index belongs to the tab and filters it was chosen under (see Investors): a new filter starts on its
  // first page in the same render, so an old offset is never sent for it.
  const key = filterKey(tab, filter);
  const [pageAt, setPageAt] = useState<{ key: string; index: number }>({ key, index: 0 });
  const pageIndex = pageAt.key === key ? pageAt.index : 0;
  const setPageIndex = (index: number) => setPageAt({ key, index });
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const query = ready ? recordsQuery(filter, target.vaultId, pageIndex * rowsPerPage, rowsPerPage) : null;
  const records = useRecords(tab, query, key);

  const [draft, setDraft] = useState<Draft>(() => draftOf(filter));
  const [rangeError, setRangeError] = useState<string | null>(null);
  useEffect(() => {
    setDraft(draftOf(forTab(parseRecordsSearch(search), tab)));
    setRangeError(null);
  }, [search, tab]);

  const go = (next: Partial<RecordsFilter>, nextTab: RecordTab = tab) =>
    navigate(recordsPath(root, nextTab, { ...filter, ...next }));

  const apply = () => {
    const range = toIsoRange(draft.from, draft.to);
    if (range?.error) {
      setRangeError(translate(`${NS}.filter.${range.error}`));
      return;
    }
    setRangeError(null);
    go({ holderId: draft.holder.trim(), status: draft.status, from: range?.from ?? '', to: range?.to ?? '' });
  };
  const clear = () => go({ ...EMPTY_FILTER, vault: filter.vault });

  const statuses = statusesOf(tab);
  const statusLabel = (s: string) =>
    translate(tab === 'subscriptions' ? `${NS}.status.${s}` : `${NS}.closure.${s}`, { blocker: '…' });
  const page = records.page;
  const refresh = () => (query ? records.refetch() : refetchVaults());

  return (
    <PermissionGuard resource={RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[{ name: translate('resource.vault'), href: '#' }, { name: translate(`${NS}.title`) }]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:refresh-bold" />}
              onClick={refresh}
              disabled={vaultsLoading || records.loading}
            >
              {translate(`${NS}.refresh`)}
            </Button>
          }
        />

        <Stack spacing={3} sx={{ mt: 3 }}>
          {/* The status belongs to a tab's own enum, so it does not follow a tab change; vault, holder and range do. */}
          <Tabs value={tab} onChange={(_, next: RecordTab) => go({ status: '' }, next)}>
            {RECORD_TABS.map((t) => (
              <Tab key={t} value={t} label={translate(`${NS}.tab.${t}`)} />
            ))}
          </Tabs>

          <Card sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              useFlexGap
              sx={{ alignItems: { md: 'flex-start' }, flexWrap: 'wrap' }}
            >
              <VaultSwitcher
                vaults={vaults}
                value={target.all ? ALL_VAULTS : (target.vaultId ?? '')}
                onChange={(next) => go({ vault: next })}
                disabled={vaultsLoading}
                allowAll
              />
              <TextField
                size="small"
                label={translate(`${NS}.filter.holder`)}
                value={draft.holder}
                onChange={(e) => setDraft({ ...draft, holder: e.target.value })}
                sx={{ minWidth: { xs: 1, md: 300 } }}
              />
              <TextField
                select
                size="small"
                label={translate(`${NS}.filter.status`)}
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                sx={{ minWidth: { xs: 1, md: 200 } }}
              >
                <MenuItem value="">{translate(`${NS}.filter.anyStatus`)}</MenuItem>
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabel(s)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="datetime-local"
                label={translate(`${NS}.filter.from`)}
                value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!rangeError}
              />
              <TextField
                size="small"
                type="datetime-local"
                label={translate(`${NS}.filter.to`)}
                value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!rangeError}
                helperText={rangeError}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={apply} disabled={!settled}>
                  {translate(`${NS}.filter.apply`)}
                </Button>
                <Button variant="text" onClick={clear} disabled={!settled}>
                  {translate(`${NS}.filter.clear`)}
                </Button>
              </Stack>
            </Stack>
          </Card>

          {vaultsError && <Alert severity="error">{vaultsError}</Alert>}
          {settled && vaults.length === 0 && !target.all && (
            <Alert severity="info">{translate(`${NS}.noVaults`)}</Alert>
          )}
          {records.loadError && <Alert severity="error">{records.loadError}</Alert>}

          {query && (
            <LoadingPanel isLoading={records.loading && !page}>
              <Stack spacing={1}>
                {page && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {translate(`${NS}.summary`, { total: formatCount(page.total) })}
                  </Typography>
                )}
                {!records.loadError &&
                  (tab === 'subscriptions' ? (
                    <SubscriptionsTable records={(page?.items ?? []) as SubscriptionRecord[]} />
                  ) : (
                    <RedemptionsTable records={(page?.items ?? []) as RedemptionRecord[]} />
                  ))}
                {page && page.total > 0 && (
                  <Card>
                    <TablePagination
                      component="div"
                      count={page.total}
                      page={pageIndex}
                      onPageChange={(_, next) => setPageIndex(next)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPageIndex(0);
                      }}
                      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                      labelRowsPerPage={translate(`${NS}.rowsPerPage`)}
                    />
                  </Card>
                )}
              </Stack>
            </LoadingPanel>
          )}
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default RecordsPage;
